import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useCallback, useMemo } from 'react';
import { styled } from 'styled-components';
import { useResourceNotepadDataContextSafe } from '../../../context/resourceDataContext.js';
import { HybridSyncProviderMode } from '../../../../utils/HybridSyncProvider.js';
import { useOptimisticNotepadStateUpdate, useMutationNotepadStateUpdate } from '../../../../mutations/useMutationNotepadStateUpdate.js';
import { HeaderWrapper } from '../../components/ModalResourceHeader.js';
import { ExpandedResourceTitle } from '../../components/ResourceTitle.js';
import { ExpandedResourceDropdownMenu } from '../../components/ExpandedResourceDropdownMenu.js';
import { AnalyticsEvents } from '../../../../../analytics/analytics.types.js';
import { useAnalytics } from '../../../../../analytics/hooks/useAnalytics.js';
import { useExpandedFdocContext } from '../../../ExpandedFdoc/ExpandedFdocProvider.js';
import { isTruthy, isDefined } from '@fabric-app/utils/guards';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import MultiplayerEditor from '../../../../../Tiptap/MultiplayerEditor.js';
import { useMutationCreateNotepadResource } from '../../../../../resources/mutations/useMutationCreateNotepadResource.js';
import { v4 } from 'uuid';
import { useExpandedResourceStore } from '../../../../../resources/stores/useExpandedResourceStore.js';
import { NotepadUpdateFailsafeService } from '../../../../../Tiptap/storage/NotepadUpdateFailsafeService.js';

const EditorWrapper = styled.div.withConfig({
  displayName: "ResourceContentNotepad__EditorWrapper",
  componentId: "sc-188430b3-0"
})([
  `height:1px;flex-grow:1;position:relative;&& [data-tiptap-container='true']{--tiptap-padding-vertical:1.5rem;--tiptap-padding-horizontal:1.5rem;}`
]);
const ResourceContentNotepad = () => {
  const { resource, disabledActions } = useResourceNotepadDataContextSafe();
  const { track } = useAnalytics();
  const mutationCreateNotepadResource = useMutationCreateNotepadResource();
  const { setToolbarHeight, isEditorFocused, setIsEditorFocused } = useExpandedFdocContext();
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [hybridSyncMode, setHybridSyncMode] = useState(HybridSyncProviderMode.FullyOffline);
  const dynamicContentRef = useRef(resource.data.preview);
  const trackAnalyticsNotepadEdit = React__default.useCallback(() => {
    track(AnalyticsEvents.NotepadEdit);
  }, [
    track
  ]);
  const trackAnalyticsNotepadEditDebounced = useDebouncedCallback(trackAnalyticsNotepadEdit, 1e3);
  const optimisticNotepadStateUpdate = useOptimisticNotepadStateUpdate();
  const { mutate: mutateNotepadState, isPending: isNotepadStatePending } = useMutationNotepadStateUpdate({
    resourceId: resource.id
  });
  const isSynced = [
    hybridSyncMode === HybridSyncProviderMode.Online,
    hybridSyncMode === HybridSyncProviderMode.FullyOffline && !hasPendingChanges && !isNotepadStatePending
  ].some(isTruthy);
  const resourceTitleRef = useRef(null);
  const [title, setTitle] = useState(resource.name ?? "");
  const onChangeTitle = useCallback((title2) => {
    if (!isDefined(title2)) return;
    setTitle(title2);
    resourceTitleRef.current?.setTitle(title2);
  }, []);
  const isNotepadLoading = useMemo(() => {
    return !isSynced && hybridSyncMode === HybridSyncProviderMode.SemiOffline || isNotepadStatePending;
  }, [
    hybridSyncMode,
    isNotepadStatePending,
    isSynced
  ]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(HeaderWrapper, {
        children: [
          /* @__PURE__ */ jsx(HeaderWrapper.Left, {
            children: /* @__PURE__ */ jsx(ExpandedResourceTitle, {
              ref: resourceTitleRef,
              onTitleChange: setTitle
            })
          }),
          /* @__PURE__ */ jsx(HeaderWrapper.Right, {
            children: /* @__PURE__ */ jsx(ExpandedResourceDropdownMenu, {})
          })
        ]
      }),
      /* @__PURE__ */ jsx(EditorWrapper, {
        "data-pan-id-ignore-expanded-resource": isEditorFocused ? "xy" : false,
        children: /* @__PURE__ */ jsx(MultiplayerEditor, {
          onModeChange: setHybridSyncMode,
          onHasPendingChanges: setHasPendingChanges,
          onOfflineChange: useCallback((update, hashes, docChanged, inMultiplayer) => {
            const changes = {
              resource,
              update,
              docChanged,
              optimisticContent: dynamicContentRef.current
            };
            if (inMultiplayer) {
              optimisticNotepadStateUpdate(changes);
              return;
            }
            mutateNotepadState(changes, {
              onSuccess: () => {
                NotepadUpdateFailsafeService.getInstance().markAsSentByHash(resource.id, hashes);
              }
            });
          }, [
            mutateNotepadState,
            optimisticNotepadStateUpdate,
            resource
          ]),
          tiptapProps: {
            editable: !disabledActions.modify,
            initialValue: resource.data.preview,
            contentClassName: "tiptap_editor_content",
            onToolbarHeightChange: setToolbarHeight,
            onUpload: async (props) => {
              const uploadId = v4();
              useExpandedResourceStore.getState().expandedResourcePendingUploads.add(uploadId);
              const removeFromPending = () => {
                useExpandedResourceStore.getState().expandedResourcePendingUploads.delete(uploadId);
              };
              const createdResource = await mutationCreateNotepadResource.mutateAsync({
                targetResourceId: resource.id,
                ...props
              }, {
                onError: removeFromPending
              });
              if (createdResource) {
                return {
                  resource: createdResource,
                  ackProcessed: removeFromPending
                };
              }
              removeFromPending();
              return null;
            },
            tabIndex: "10",
            autoFocus: false,
            onEditorFocusChange: setIsEditorFocused,
            onChange: (value) => {
              dynamicContentRef.current = value;
              trackAnalyticsNotepadEditDebounced();
            },
            onCreate: useCallback((editor) => {
              editor.commands.defineResourceId(resource.id);
            }, [
              resource.id
            ]),
            title,
            onTitleChange: onChangeTitle,
            isLoading: isNotepadLoading
          }
        })
      })
    ]
  });
};

export { ResourceContentNotepad };
//# sourceMappingURL=ResourceContentNotepad.js.map
